import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import {
  getUsers,
  deleteUser,
  approveUser,
  blockUser,
} from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';

const UsersPage = () => {
  const { translate } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null, userEmail: '' });
  const navigate = useNavigate();

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDeleteClick = (id, email) => {
    setConfirmDialog({
      open: true,
      userId: id,
      userEmail: email,
    });
  };

  const handleDelete = async () => {
    const { userId } = confirmDialog;
    setConfirmDialog({ open: false, userId: null, userEmail: '' });

    try {
      await deleteUser(userId);
      setSnackbar({
        open: true,
        message: translate('users.deleted'),
        severity: 'success',
      });
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDialog({ open: false, userId: null, userEmail: '' });
  };

  const handleApprove = async (id) => {
    try {
      await approveUser(id);
      setSnackbar({
        open: true,
        message: translate('users.approved'),
        severity: 'success',
      });
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleBlock = async (id) => {
    try {
      await blockUser(id);
      setSnackbar({
        open: true,
        message: translate('users.blocked'),
        severity: 'success',
      });
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'BLOCKED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProfileLabel = (profile) => {
    switch (profile) {
      case 'ADMIN':
        return translate('users.admin');
      case 'MANAGER':
        return translate('users.manager');
      case 'SIMPLE':
        return translate('users.simple');
      default:
        return profile;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'APPROVED':
        return translate('users.approved');
      case 'BLOCKED':
        return translate('users.blocked');
      case 'PENDING':
        return translate('users.pending');
      default:
        return status;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {translate('users.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/new')}
        >
          {translate('common.new')} {translate('users.title')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('users.email')}</TableCell>
              <TableCell>{translate('users.profile')}</TableCell>
              <TableCell>{translate('users.status')}</TableCell>
              <TableCell align="right">{translate('users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getProfileLabel(user.profile)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(user.status)}
                      color={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/users/${user.id}/edit`)}
                    >
                      <EditIcon />
                    </IconButton>
                    {user.status !== 'APPROVED' && (
                      <IconButton
                        size="small"
                        onClick={() => handleApprove(user.id)}
                        color="success"
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    )}
                    {user.status !== 'BLOCKED' && (
                      <IconButton
                        size="small"
                        onClick={() => handleBlock(user.id)}
                        color="error"
                      >
                        <BlockIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(user.id, user.email)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o usuário "${confirmDialog.userEmail}"?`}
        onConfirm={handleDelete}
        onCancel={handleDeleteCancel}
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;



